import React from 'react';
import classNames from 'classnames';

import { action } from 'mobx';
import { observer } from 'mobx-react';

import { useStyles } from 'ui/base/theme';
import { BusyStore } from 'ui/base/button';
import { ChoiceStore } from 'ui/base/input';
import { StandardPage } from 'ui/base/page/standard_page';

import { RadioInput } from 'ui/standard/input/radio_input';

import { ActiveButton, DefaultButton } from 'ui/public/button';
import { ErrorMessage } from 'ui/public/error_message/error_message';

import { Optional } from 'services/base/types';
import { GlobalLocaleStore, localeToLanguageCode } from 'services/base/i18n';

import {
  PlanFirewallBilling,
  PlanFirewallPackage,
} from 'services/accounts/types';

import { PlanFirewallService } from 'services/accounts/plans_service';

import {
  getPlanFirewallBillingInfo,
  getPlanFirewallBillingName,
  getPlanFirewallPackageTier,
  getPlanFirewallPriceActive,
  getPlanFirewallPricePerText,
  getPlanFirewallSubscriptionActive,
} from 'services/accounts/logic/plans/plan_firewall';

import { Currency } from 'assets/enums/currencies/currencies';

import {
  themedPlanFirewallBillingIconStyles,
  getPlanFirewallBillingIcon,
} from 'assets/enums/plans/firewall/plan_firewall';

import { PlanItemFirewallBaseOptionalModalProps } from '../plan_item_firewall';

import ManagePlansMessages from '../../../../manage_plans_messages';
import PlanFirewallMessages from '../plan_item_firewall_messages';
import Messages from './plan_firewall_billing_modal_messages';

type PlanItemFirewallBillingInputRowProps = PlanItemFirewallBaseOptionalModalProps & {
  choices: ChoiceStore<PlanFirewallBilling>;
  choice: PlanFirewallBilling;
};
const PlanItemFirewallBillingInputRow = observer(({ styles, accounts, plan, store, choices, choice } : PlanItemFirewallBillingInputRowProps) => {
  const icons = useStyles(themedPlanFirewallBillingIconStyles);

  // The list of selectable billing options should be strongly bound to the selected package.
  // This is because the billing modal comes after the package modal. This means that we do not
  // have to find the closest available package option for this billing.

  const package_id = store.package_store.value ?? PlanFirewallPackage.BASIC;

  const billing_name = getPlanFirewallBillingName(choice);
  const billing_icon = getPlanFirewallBillingIcon(icons, choice);
  const billing_info = getPlanFirewallBillingInfo(accounts, choice, package_id);
  const billing_price = getPlanFirewallPricePerText(accounts, package_id, choice, Currency.USD, false);
  const billing_active = getPlanFirewallPriceActive(accounts, package_id, choice);

  const selected = choice === choices.selected;

  const disabled = (
    !billing_price || !billing_active || (plan?.license && ((
      getPlanFirewallPackageTier(store.package_store.value) <
      getPlanFirewallPackageTier(plan.license.package)
    ) || (
      getPlanFirewallPackageTier(store.package_store.value) ===
      getPlanFirewallPackageTier(plan.license.package) &&
      choice !== PlanFirewallBilling.LIFETIME
    )))
  );

  const choice_store = React.useCallback(action(() => {
    if (!disabled) choices.setSelected(choice);
  }), [choices]);

  return (<>
    {!disabled && (
      <div onClick={choice_store}
           className={classNames(!disabled && styles.clickable,
                                 selected ? styles.cardSelected :
                                 disabled ? styles.cardDisabled :
                                 styles.cardDefault, styles.field)}>
        <img className={styles.cardIcon} src={billing_icon} alt='Billing Icon' />
        <div className={styles.cardText}>
          <div className={styles.cardName}>
            {billing_name}
            <span className={styles.cardInfo}>
              ({billing_price ? billing_price : PlanFirewallMessages.priceUnavailable()})
            </span>
          </div>
          <div className={styles.cardInfo}>
            {billing_info}
          </div>
        </div>
        <RadioInput
          store={choices}
          choice={choice}
        />
      </div>
    )}
  </>);
});

type PlanItemFirewallBillingModalProps = PlanItemFirewallBaseOptionalModalProps;
export const PlanItemFirewallBillingModal = observer((props: PlanItemFirewallBillingModalProps) => {
  const { styles, accounts, pangolin, plan, store, modal, root } = props;

  const disabled = store.billing_store.value === undefined;

  const licensePackage = plan?.license?.package;
  const subscriptionPackage = plan?.subscription?.package;
  const targetPackage = store.package_store.value;

  const [busy] = React.useState(new BusyStore());
  const [error, setError] = React.useState<Optional<string>>(undefined);
  const [warning, setWarning] = React.useState<Optional<string>>(undefined);

  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  // Handle warning
  React.useEffect(() => {
    if (plan && getPlanFirewallSubscriptionActive(plan)) {
      const targetPackageTier = getPlanFirewallPackageTier(targetPackage);
      const subscriptionPackageTier = getPlanFirewallPackageTier(subscriptionPackage);
      if (targetPackageTier <= subscriptionPackageTier) {
        // All scenarios where the current subscription is retained are
        // conditional on whether the user is purchasing a package tier
        // lower than the current subscription package tier.
        //
        // You may refer to the scenarios outlined in the previewPlanChange
        // method in: services/accounts/logic/plans/plan_firewall.ts
        if (plan.subscription) {
          setWarning(PlanFirewallMessages.warningEffectivity(
            plan.subscription.valid_until.toLocaleString(locale, format)
          ));
        }
      } else {
        setWarning(undefined);
      }
    }
  }, [plan, licensePackage, subscriptionPackage, targetPackage, locale, format]);

  // Handle navigation
  const onNext = React.useCallback(async() => {
    if (!disabled) {
      // Check if the user has not changed the plan details
      const changePlanDetails = (
        store.package_store.value !== store.package_default ||
        store.billing_store.value !== store.billing_default
      );

      if (changePlanDetails) {
        // If plan details have changed...

        // Set subsequent steps to undefined
        store.preview_is_trialing_store.setValue(false);
        store.preview_trial_until_store.setValue(undefined);
        store.payment_store.setSelected(undefined);
        store.preview_charges_store.setValue(undefined);
        store.preview_refunds_store.setValue(undefined);
        store.preview_notes_store.setValue(undefined);
      } else {
        // If the plan details are the same...

        // Set subsequent steps to default
        store.payment_store.setSelected(store.payment_default);
      }

      // Check if the user is switching to an owned license
      const changeToOwnedLicense = (
        store.billing_store.value === PlanFirewallBilling.LIFETIME && (
          getPlanFirewallPackageTier(store.package_store.value) <=
          getPlanFirewallPackageTier(plan?.license?.package)
        )
      );

      if (changeToOwnedLicense) {
        // If the user is switching to an owned license...

        // Configure store for owned license
        store.payment_store.setSelected(undefined);

        // Clear error string
        setError(undefined);

        // Clear preview data
        store.preview_is_trialing_store.setValue(false);
        store.preview_trial_until_store.setValue(undefined);
        store.preview_charges_store.setValue(undefined);
        store.preview_refunds_store.setValue(undefined);
        store.preview_notes_store.setValue(undefined);

        // Retrieve invoice preview
        const preview = await busy.perform(async() => {
          try {
            modal.freezeModal();
            return await PlanFirewallService.preview(
              accounts, pangolin,
              store.payment_store.value!,
              store.package_store.value!,
              store.billing_store.value!,
            );
          } finally {
            modal.unfreezeModal();
          }
        });

        if (preview.success) {
          // Go to confirm modal on success
          store.preview_is_trialing_store.setValue(preview.is_trialing);
          store.preview_trial_until_store.setValue(preview.trial_until);
          store.preview_charges_store.setValue(preview.charges);
          store.preview_refunds_store.setValue(preview.refunds);
          store.preview_notes_store.setValue(preview.notes);
          modal.history.push(`${root}/confirm`);
        } else {
          // Set error string on failure
          setError(PlanFirewallMessages.errorInvoice());
        }
      } else {
        // If the user is switching to a new arrangement...

        // Go to payment modal
        modal.history.push(`${root}/payment`);
      }
    }
  }, [modal, accounts, pangolin, root, busy, store, plan, disabled]);

  return (
    <div className={classNames(styles.modalContainerBig, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.name()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <div className={styles.section}>
            <PlanItemFirewallBillingInputRow
              choice={PlanFirewallBilling.MONTHLY}
              choices={store.billing_store}
              {...props}
            />
            <PlanItemFirewallBillingInputRow
              choice={PlanFirewallBilling.YEARLY}
              choices={store.billing_store}
              {...props}
            />
            <PlanItemFirewallBillingInputRow
              choice={PlanFirewallBilling.BIENNIAL}
              choices={store.billing_store}
              {...props}
            />
            <PlanItemFirewallBillingInputRow
              choice={PlanFirewallBilling.TRIENNIAL}
              choices={store.billing_store}
              {...props}
            />
            <PlanItemFirewallBillingInputRow
              choice={PlanFirewallBilling.LIFETIME}
              choices={store.billing_store}
              {...props}
            />
            {error && (
              <div className={styles.notes}>
                <ErrorMessage message={error} />
              </div>
            )}
            {warning && (
              <div className={styles.notes}>
                <ErrorMessage message={warning} />
              </div>
            )}
          </div>
          <ActiveButton onClick={onNext} disabled={disabled}>
            {ManagePlansMessages.actionNext()}
          </ActiveButton>
          <DefaultButton type='button' onClick={modal.history.goBack}>
            {ManagePlansMessages.actionBack()}
          </DefaultButton>
        </div>
      </StandardPage>
    </div>
  );
});
